import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { USER_DETAIL } from "@/constants/testIds";
import { Clock3, Zap, PowerOff, BellOff, ShieldAlert, MessageCircle, Mail, RefreshCw } from "lucide-react";

/**
 * Compact "next AI/cadence action" widget — designed for the right rail beneath the AI Status card.
 *
 * Fetches /trial-users/{userId}/next-action, re-renders the countdown client-side
 * every second, and re-fetches from the server every 30s or when `refreshKey` changes.
 */

function formatCountdown(seconds) {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds <= 0) return "any moment";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function humanTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function NextActionBadge({ userId, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [, forceTick] = useState(0);
  const anchorRef = useRef({ scheduled: null });

  const fetchData = async () => {
    if (!userId) return;
    setRefreshing(true);
    try {
      const { data: d } = await api.get(`/trial-users/${userId}/next-action`);
      setData(d);
      anchorRef.current = {
        scheduled: d.scheduled_at ? new Date(d.scheduled_at) : null,
      };
    } catch { /* non-critical */ }
    finally { setRefreshing(false); }
  };

  useEffect(() => { fetchData(); }, [userId, refreshKey]); // eslint-disable-line

  useEffect(() => {
    const iv = setInterval(fetchData, 30000);
    return () => clearInterval(iv);
  }, [userId]); // eslint-disable-line

  useEffect(() => {
    const iv = setInterval(() => forceTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  if (!data) return null;

  // ---- Blocked variants ----
  if (data.blocked) {
    const cfg = ({
      global_kill_switch: { Icon: ShieldAlert, headline: "AI globally OFF",   hint: "Turn Global Kill Switch off on AI Control." },
      cadence_disabled:   { Icon: PowerOff,    headline: "Cadence auto-send OFF", hint: "AI Control → Cadence auto-send." },
      dnd:                { Icon: BellOff,     headline: "DND",                hint: data.reason_label },
      ai_paused:          { Icon: PowerOff,    headline: "Paused",             hint: 'Click "Resume AI" above.' },
      cancelled:          { Icon: ShieldAlert, headline: "Cancelled",          hint: "No cadence for cancelled users." },
      no_cadence:         { Icon: ShieldAlert, headline: "No cadence",         hint: data.reason_label },
      cadence_complete:   { Icon: Zap,         headline: "Cadence complete",   hint: data.reason_label },
      invalid_join_date:  { Icon: ShieldAlert, headline: "Missing join date",  hint: null },
    })[data.reason] || { Icon: ShieldAlert, headline: data.reason_label || "Blocked", hint: null };
    const { Icon, headline, hint } = cfg;
    return (
      <div
        data-testid={USER_DETAIL.nextActionBadge}
        className="border-t border-slate-200 mt-4 pt-4"
      >
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Next AI / cadence</div>
          <button onClick={fetchData} disabled={refreshing}
            className="p-1 rounded hover:bg-slate-100 disabled:opacity-60" title="Refresh">
            <RefreshCw className={`h-3 w-3 text-slate-500 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-slate-500 shrink-0" />
          <div className="text-sm font-semibold text-slate-800">{headline}</div>
        </div>
        {hint && <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">{hint}</div>}
      </div>
    );
  }

  // ---- Scheduled variant — live countdown ----
  const seconds = anchorRef.current.scheduled
    ? Math.round((anchorRef.current.scheduled - new Date()) / 1000)
    : data.seconds_until;
  const late = seconds !== null && seconds < 0;
  const ChannelIcon = data.channel === "email" ? Mail : MessageCircle;
  const cadenceLabel = ({
    trial_conversion: "Trial-conversion",
    post_subscription: "Post-subscription",
    win_back: "Win-back",
  })[data.cadence_kind] || "Cadence";

  return (
    <div
      data-testid={USER_DETAIL.nextActionBadge}
      className="border-t border-slate-200 mt-4 pt-4"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Next AI / cadence</div>
        <button
          onClick={fetchData}
          disabled={refreshing}
          className="p-1 rounded hover:bg-slate-100 disabled:opacity-60"
          title="Refresh"
        >
          <RefreshCw className={`h-3 w-3 text-slate-500 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="flex items-baseline gap-2 mb-1">
        <Clock3 className={`h-4 w-4 shrink-0 ${late ? "text-orange-600" : "text-indigo-600"}`} />
        <div
          data-testid={USER_DETAIL.nextActionCountdown}
          className={`font-heading font-extrabold text-2xl tabular ${late ? "text-orange-600" : "text-indigo-700"}`}
        >
          {late ? "on next tick" : `in ${formatCountdown(seconds)}`}
        </div>
      </div>

      <div className="text-[11px] text-slate-600 leading-relaxed">
        <span className="inline-flex items-center gap-1">
          <ChannelIcon className="h-3 w-3" /> {data.channel}
        </span>
        <span className="text-slate-300 mx-1">·</span>
        {data.step_label}
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1 flex-wrap">
        <span className="uppercase font-semibold tracking-widest text-[9px] text-slate-500">{cadenceLabel}</span>
        {anchorRef.current.scheduled && (
          <>
            <span className="text-slate-300">·</span>
            <span>@ {humanTime(data.scheduled_at)}</span>
          </>
        )}
      </div>
    </div>
  );
}
